import { cx } from '../cx'
import styles from './FateToken.module.css'

/** The coins the pool is broken into, largest first — the order the greedy pass wants. */
export const FATE_COIN_DENOMINATIONS = [50, 10, 5, 1] as const

export type FateCoinValue = (typeof FATE_COIN_DENOMINATIONS)[number]

/**
 * Breaks a count into coins, largest first.
 *
 * Deliberately greedy rather than minimal: with 1 / 5 / 10 / 50 the greedy pass
 * *is* minimal, and even where it would not be, a player reading a row of coins
 * cares that it obviously adds up — not that it used the fewest pieces.
 */
export function fateCoins(points: number): FateCoinValue[] {
  const coins: FateCoinValue[] = []
  let left = Math.max(0, Math.floor(points))

  for (const denomination of FATE_COIN_DENOMINATIONS) {
    while (left >= denomination) {
      coins.push(denomination)
      left -= denomination
    }
  }

  return coins
}

/** Coin diameter per denomination. Bigger value, bigger coin — the read before the numeral. */
const COIN_SIZE: Record<FateCoinValue, number> = { 1: 24, 5: 28, 10: 32, 50: 38 }

export interface FateTokenProps {
  /** Solid gold ring when available; dashed and dim when spent (spec §4). */
  available: boolean
  /** What the coin is worth. Sets its size and the figure struck on it. */
  value?: FateCoinValue
  size?: number
  className?: string | undefined
}

/**
 * A single fate-point coin: gold ring over near-black, pentacle etched inside,
 * its denomination struck across the middle.
 */
export function FateToken({ available, value = 1, size, className }: FateTokenProps) {
  const diameter = size ?? COIN_SIZE[value]

  return (
    <svg
      className={cx(styles.token, available ? styles.available : styles.spent, className)}
      width={diameter}
      height={diameter}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <circle className={styles.face} cx="16" cy="16" r="14" />
      <circle
        className={styles.ring}
        cx="16"
        cy="16"
        r="14"
        fill="none"
        strokeDasharray={available ? undefined : '3 3'}
      />
      {/* The milled inner ring is what separates a 10 from a 1 at arm's length,
          before anyone reads the figure. */}
      {value >= 10 && <circle className={styles.inner} cx="16" cy="16" r="11" fill="none" />}
      {/* Pentacle: five points on a circle, connected every second point. Dropped
          to an etch here — the figure is what the eye needs first. */}
      <path
        className={styles.pentacle}
        d="M16 6.5 L23.13 27.1 L5.6 14.36 L26.4 14.36 L8.87 27.1 Z"
        fill="none"
      />
      <text
        className={styles.figure}
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={value >= 10 ? 12 : 15}
      >
        {value}
      </text>
    </svg>
  )
}

/**
 * How many coins are worth drawing.
 *
 * A fate pool has no ceiling, and past this many the row stops being something
 * anyone reads at a glance — it collapses to a single coin standing in as the
 * motif, with the number itself carrying the count. Two-digit pools never reach
 * this: 99 breaks into ten coins.
 */
export const FATE_TOKEN_CAP = 12

export interface FateTokenRowProps {
  current: number
  max: number
  /** Coins to draw before collapsing. Defaults to {@link FATE_TOKEN_CAP}. */
  cap?: number
  /** The running `n / max` beside the row. Off when a caller shows the count itself. */
  showCount?: boolean
  className?: string | undefined
}

/**
 * The token row with its running `n / max`, per spec §5.2 "Fate-point economy".
 *
 * The pool is struck in coins; anything the character is short of `max` follows
 * as dashed ones, so the gap to a full refresh is visible rather than arithmetic.
 * `current` and `max` come straight from the character's `fate` section — the
 * client does not compute either.
 */
export function FateTokenRow({
  current,
  max,
  cap = FATE_TOKEN_CAP,
  showCount = true,
  className,
}: FateTokenRowProps) {
  const held = fateCoins(current)
  const short = fateCoins(Math.max(max - current, 0))
  const collapsed = held.length + short.length > cap

  return (
    <div className={cx(styles.row, className)}>
      <div className={styles.tokens}>
        {collapsed ? (
          <FateToken available={current > 0} value={FATE_COIN_DENOMINATIONS[0]} />
        ) : (
          <>
            {held.map((value, i) => (
              <FateToken key={`held-${i}`} available value={value} />
            ))}
            {short.map((value, i) => (
              <FateToken key={`short-${i}`} available={false} value={value} />
            ))}
          </>
        )}
      </div>
      {showCount && (
        <span className={styles.count}>
          <span className={styles.countCurrent}>{current}</span>
          <span className={styles.countSep}> / {max}</span>
          <span className="sr-only"> fate points available</span>
        </span>
      )}
    </div>
  )
}
